// src/pages/finanzas/Finanzas.js
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'

const EMPTY = {
  tipo: 'ingreso', categoria_id: '', cuenta_id: '',
  monto: '', descripcion: '', fecha: new Date().toISOString().slice(0,10),
  referencia: '', modulo: '', notas: ''
}

export default function Finanzas() {
  const { profile } = useAuth()
  const canWrite = ['admin', 'finanzas'].includes(profile?.rol)

  const [transacciones, setTransacciones] = useState([])
  const [categorias, setCategorias] = useState([])
  const [cuentas, setCuentas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filtroTipo, setFiltroTipo] = useState('')
  const [totales, setTotales] = useState({ ingresos: 0, gastos: 0 })

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    const [tRes, cRes, cuRes] = await Promise.all([
      supabase.from('transacciones').select('*, categoria:categorias_financieras(nombre, color), cuenta:cuentas_bancarias(banco)').order('fecha', { ascending: false }).limit(200),
      supabase.from('categorias_financieras').select('*').order('nombre'),
      supabase.from('cuentas_bancarias').select('*').eq('activa', true),
    ])
    const data = tRes.data ?? []
    setTransacciones(data)
    setCategorias(cRes.data ?? [])
    setCuentas(cuRes.data ?? [])
    setTotales({
      ingresos: data.filter(t => t.tipo === 'ingreso').reduce((s, t) => s + Number(t.monto), 0),
      gastos: data.filter(t => t.tipo === 'gasto').reduce((s, t) => s + Number(t.monto), 0),
    })
    setLoading(false)
  }

  function openNew() { setForm(EMPTY); setEditId(null); setModal(true) }
  function openEdit(t) {
    setForm({
      tipo: t.tipo, categoria_id: t.categoria_id ?? '', cuenta_id: t.cuenta_id ?? '',
      monto: t.monto, descripcion: t.descripcion, fecha: t.fecha,
      referencia: t.referencia ?? '', modulo: t.modulo ?? '', notas: ''
    })
    setEditId(t.id); setModal(true)
  }
  function onChange(e) { setForm(f => ({ ...f, [e.target.name]: e.target.value })) }

  async function save() {
    if (!form.monto || !form.descripcion || !form.fecha) return alert('Monto, descripción y fecha son obligatorios')
    setSaving(true)
    const payload = { ...form, registrado_por: profile.id, monto: parseFloat(form.monto) }
    if (editId) await supabase.from('transacciones').update(payload).eq('id', editId)
    else await supabase.from('transacciones').insert([payload])
    setSaving(false); setModal(false); loadData()
  }

  async function del(id) {
    if (!window.confirm('¿Eliminar esta transacción?')) return
    await supabase.from('transacciones').delete().eq('id', id)
    loadData()
  }

  const catsByTipo = form.tipo ? categorias.filter(c => c.tipo === form.tipo) : categorias
  const filtered = transacciones.filter(t => !filtroTipo || t.tipo === filtroTipo)
  const saldo = totales.ingresos - totales.gastos

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">💰 Finanzas</div>
          <div className="page-subtitle">Ingresos, gastos y flujo de caja</div>
        </div>
        {canWrite && <button className="btn btn-primary" onClick={openNew}>+ Nueva transacción</button>}
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dcfce7' }}>💹</div>
          <div>
            <div className="stat-value" style={{ color: 'var(--brand-600)' }}>${totales.ingresos.toLocaleString('es', { maximumFractionDigits: 0 })}</div>
            <div className="stat-label">Total ingresos</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fee2e2' }}>📉</div>
          <div>
            <div className="stat-value" style={{ color: 'var(--accent-red)' }}>${totales.gastos.toLocaleString('es', { maximumFractionDigits: 0 })}</div>
            <div className="stat-label">Total gastos</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: saldo >= 0 ? '#dcfce7' : '#fee2e2' }}>⚖️</div>
          <div>
            <div className="stat-value" style={{ color: saldo >= 0 ? 'var(--brand-600)' : 'var(--accent-red)' }}>
              {saldo >= 0 ? '+' : ''}${saldo.toLocaleString('es', { maximumFractionDigits: 0 })}
            </div>
            <div className="stat-label">Saldo neto</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[['', 'Todos'], ['ingreso', '💚 Ingresos'], ['gasto', '🔴 Gastos']].map(([k, v]) => (
            <button key={k} className={`btn btn-sm ${filtroTipo === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFiltroTipo(k)}>{v}</button>
          ))}
        </div>
        <div className="table-wrap">
          {loading
            ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-dark" style={{ margin: 'auto' }} /></div>
            : (
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Categoría</th>
                    <th>Descripción</th>
                    <th>Cuenta</th>
                    <th>Monto</th>
                    {canWrite && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={7}><div className="empty-state">Sin transacciones</div></td></tr>}
                  {filtered.map(t => (
                    <tr key={t.id}>
                      <td>{new Date(t.fecha).toLocaleDateString('es')}</td>
                      <td><span className={`badge ${t.tipo === 'ingreso' ? 'badge-green' : 'badge-red'}`}>{t.tipo}</span></td>
                      <td>
                        {t.categoria && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.categoria.color, display: 'inline-block' }} />
                            {t.categoria.nombre}
                          </span>
                        )}
                      </td>
                      <td>{t.descripcion}</td>
                      <td>{t.cuenta?.banco ?? '—'}</td>
                      <td style={{ fontWeight: 600, color: t.tipo === 'ingreso' ? 'var(--brand-600)' : 'var(--accent-red)' }}>
                        {t.tipo === 'ingreso' ? '+' : '-'}${Number(t.monto).toLocaleString('es', { maximumFractionDigits: 2 })}
                      </td>
                      {canWrite && (
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}>✏️</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => del(t.id)}>🗑️</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{editId ? 'Editar transacción' : 'Nueva transacción'}</div>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select className="form-select" name="tipo" value={form.tipo} onChange={onChange}>
                  <option value="ingreso">💚 Ingreso</option>
                  <option value="gasto">🔴 Gasto</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Categoría</label>
                <select className="form-select" name="categoria_id" value={form.categoria_id} onChange={onChange}>
                  <option value="">Sin categoría</option>
                  {catsByTipo.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fecha *</label>
                <input className="form-input" type="date" name="fecha" value={form.fecha} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Monto (USD) *</label>
                <input className="form-input" type="number" step="0.01" name="monto" value={form.monto} onChange={onChange} placeholder="0.00" />
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Descripción *</label>
                <input className="form-input" name="descripcion" value={form.descripcion} onChange={onChange} placeholder="Detalle de la transacción" />
              </div>
              <div className="form-group">
                <label className="form-label">Cuenta bancaria</label>
                <select className="form-select" name="cuenta_id" value={form.cuenta_id} onChange={onChange}>
                  <option value="">Sin cuenta</option>
                  {cuentas.map(c => <option key={c.id} value={c.id}>{c.banco} ({c.moneda})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Referencia</label>
                <input className="form-input" name="referencia" value={form.referencia} onChange={onChange} placeholder="Nro. factura, cheque..." />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
